import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './Button';
import { cn } from '@/lib/cn';

/**
 * Pembungkus tabel. `overflow-x-auto` di sini memastikan tabel lebar
 * menggulir di dalam wadahnya sendiri - bukan memaksa seluruh halaman
 * menggulir ke samping, yang merusak tata letak di ponsel.
 */
export const TableWrapper = ({ children, className }) => (
  <div className={cn('w-full overflow-x-auto', className)}>{children}</div>
);

export const Table = ({ children, className }) => (
  <table className={cn('w-full border-collapse text-sm', className)}>{children}</table>
);

export const Th = ({ children, className, align = 'left', ...props }) => (
  <th
    scope="col"
    className={cn(
      'border-b border-[var(--border-subtle)] px-5 py-3',
      'text-[11px] font-bold tracking-wider text-[var(--text-muted)] uppercase',
      align === 'right' && 'text-right',
      align === 'center' && 'text-center',
      align === 'left' && 'text-left',
      className,
    )}
    {...props}
  >
    {children}
  </th>
);

export const Td = ({ children, className, align = 'left', ...props }) => (
  <td
    className={cn(
      'px-5 py-3.5 align-middle text-[var(--text-secondary)]',
      align === 'right' && 'text-right',
      align === 'center' && 'text-center',
      className,
    )}
    {...props}
  >
    {children}
  </td>
);

export const Tr = ({ children, className, onClick, ...props }) => (
  <tr
    onClick={onClick}
    className={cn(
      'border-b border-[var(--border-subtle)] last:border-0',
      'transition-colors duration-100',
      onClick && 'cursor-pointer hover:bg-[var(--surface-sunken)]',
      className,
    )}
    {...props}
  >
    {children}
  </tr>
);

/**
 * Paginasi. Menampilkan maksimal tujuh tombol dengan elipsis, jadi lebarnya
 * tetap sama entah ada 3 halaman atau 3.000.
 */
export const Pagination = ({ page, totalPages, total, limit, onPageChange, className }) => {
  if (!totalPages || totalPages <= 1) {
    return total ? (
      <div className={cn('px-5 py-3.5 text-sm text-[var(--text-muted)]', className)}>
        Menampilkan {total.toLocaleString('id-ID')} data
      </div>
    ) : null;
  }

  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

/** Menyusun daftar nomor halaman dengan elipsis di tempat yang tepat. */
const buildPageList = (page, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  if (page <= 4) return [1, 2, 3, 4, 5, '…', totalPages];
  if (page >= totalPages - 3) {
    return [1, '…', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, '…', page - 1, page, page + 1, '…', totalPages];
};

  const pages = buildPageList(page, totalPages);

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-between gap-3 px-5 py-3.5 sm:flex-row',
        className,
      )}
    >
      <p className="text-sm text-[var(--text-muted)] tabular">
        Menampilkan {from.toLocaleString('id-ID')}–{to.toLocaleString('id-ID')} dari{' '}
        {total.toLocaleString('id-ID')}
      </p>

      <nav className="flex items-center gap-1" aria-label="Navigasi halaman">
        <Button
          variant="secondary"
          size="xs"
          iconOnly
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label="Halaman sebelumnya"
        >
          <ChevronLeft className="size-4" />
        </Button>

        {pages.map((entry, index) =>
          entry === '…' ? (
            <span
              key={`gap-${index}`}
              className="px-1.5 text-sm text-[var(--text-muted)]"
              aria-hidden="true"
            >
              …
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              onClick={() => onPageChange(entry)}
              aria-current={entry === page ? 'page' : undefined}
              aria-label={`Halaman ${entry}`}
              className={cn(
                'size-7 rounded-md text-xs font-semibold tabular transition-colors',
                entry === page
                  ? 'bg-brand-950 text-white dark:bg-accent-600'
                  : 'text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]',
              )}
            >
              {entry}
            </button>
          ),
        )}

        <Button
          variant="secondary"
          size="xs"
          iconOnly
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          aria-label="Halaman berikutnya"
        >
          <ChevronRight className="size-4" />
        </Button>
      </nav>
    </div>
  );
};

